import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Search } from 'lucide-react';
import { useHomesList } from '@/features/homes/api';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

export const Route = createFileRoute('/_authed/homes/')({
  component: HomesPage,
});

function HomesPage() {
  const [query, setQuery] = useState('');
  const trimmed = query.trim();
  const { data, isLoading, isError, error } = useHomesList({ search: trimmed || undefined });
  const homes = data?.homes ?? [];
  const total = data?.total ?? 0;
  const navigate = useNavigate();

  const goToHome = (id: string) => void navigate({ to: '/homes/$id', params: { id } });

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="font-serif text-3xl leading-tight">Homes</h1>
        {!isLoading && (
          <p className="text-ink-soft text-sm mt-1">
            {total} home{total === 1 ? '' : 's'}
          </p>
        )}
      </header>

      <div className="mb-4 max-w-md">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
            <Search size={16} />
          </div>
          <Input
            placeholder="Search by name, code, or member…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="homes-search-input"
          />
        </div>
      </div>

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : isError ? (
          <div className="p-6 text-ink-soft">
            Failed to load homes{error instanceof Error ? `: ${error.message}` : '.'}
          </div>
        ) : homes.length === 0 ? (
          <div className="p-6 text-ink-soft">
            {trimmed ? 'No matches.' : 'No homes yet.'}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                    style={{ borderColor: 'var(--color-hairline)' }}
                  >
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Members</th>
                    <th className="px-4 py-3">Invite code</th>
                    <th className="px-4 py-3">Shared recipes</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Last shared</th>
                  </tr>
                </thead>
                <tbody>
                  {homes.map((h) => (
                    <tr
                      key={h.id}
                      data-testid={`home-row-desktop-${h.id}`}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open ${h.name}`}
                      className="border-b last:border-b-0 hover:bg-cream-deep/60 cursor-pointer"
                      style={{ borderColor: 'var(--color-hairline)' }}
                      onClick={() => goToHome(h.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToHome(h.id);
                        }
                      }}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{h.name}</div>
                        <div className="font-mono text-xs text-ink-faint break-all">{h.id}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-soft tabular-nums">{h.memberCount}</td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-soft">
                        {h.inviteCode ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-ink-soft tabular-nums">
                        {h.sharedRecipeCount}
                      </td>
                      <td className="px-4 py-3 text-ink-soft font-mono text-xs">
                        {formatDate(h.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-ink-soft font-mono text-xs">
                        {h.lastSharedRecipeAt ? formatDate(h.lastSharedRecipeAt) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <ul
              className="md:hidden divide-y"
              style={{ borderColor: 'var(--color-hairline)' }}
            >
              {homes.map((h) => (
                <li
                  key={h.id}
                  data-testid={`home-row-mobile-${h.id}`}
                  className="px-4 py-4 cursor-pointer"
                  onClick={() => goToHome(h.id)}
                >
                  <Link
                    to="/homes/$id"
                    params={{ id: h.id }}
                    className="block"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="font-medium">{h.name}</div>
                    <div className="text-ink-soft text-sm mt-0.5">
                      {h.memberCount} member{h.memberCount === 1 ? '' : 's'}
                      {h.inviteCode ? ` · ${h.inviteCode}` : ''}
                    </div>
                    <div className="text-ink-faint font-mono text-xs mt-1">
                      {formatDate(h.createdAt)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}
